import {
  format,
  getDaysInMonth,
  startOfMonth,
  getDay,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  addDays,
  parseISO,
  parse,
  isSameDay,
  isToday as isTodayDateFns,
  isSameMonth,
  isSameYear,
  addMinutes,
} from 'date-fns';

/**
 * Get the number of days in a month
 * @param {Date} date - The date to check
 * @returns {number} Number of days in the month
 */
export const getDaysInMonthHelper = (date) => {
  return getDaysInMonth(date);
};

/**
 * Get the day of the week for the first day of the month (0-6, Sunday-Saturday)
 * @param {Date} date - The date to check
 * @returns {number} Day of the week (0-6)
 */
export const getFirstDayOfMonth = (date) => {
  return getDay(startOfMonth(date));
};

/**
 * Get formatted month name and year
 * @param {Date} date - The date to format
 * @returns {string} Formatted month and year (e.g., "January 2024")
 */
export const getMonthName = (date) => {
  return format(date, 'MMMM yyyy');
};

/**
 * Navigate to next/previous month
 * @param {Date} currentDate - Current date
 * @param {number} direction - Direction to navigate (1 for next, -1 for previous)
 * @returns {Date} New date after navigation
 */
export const navigateMonth = (currentDate, direction) => {
  return addMonths(currentDate, direction);
};

/**
 * Navigate to next/previous week
 * @param {Date} currentWeekStart - Current week start date
 * @param {number} direction - Direction to navigate (1 for next, -1 for previous)
 * @returns {Date} New date after navigation
 */
export const navigateWeek = (currentWeekStart, direction) => {
  return addWeeks(currentWeekStart, direction);
};

/**
 * Get all days in a week starting from a given date
 * @param {Date} startDate - Start date of the week
 * @returns {Date[]} Array of dates for the week
 */
export const getWeekDays = (startDate) => {
  return eachDayOfInterval({
    start: startDate,
    end: endOfWeek(startDate, { weekStartsOn: 0 }),
  });
};

/**
 * Get formatted week range string
 * @param {Date} startDate - Start date of the week
 * @returns {string} Formatted week range (e.g., "Jan 1-7, 2024" or "Dec 30 - Jan 5, 2024")
 */
export const getWeekRange = (startDate) => {
  const endDate = addDays(startDate, 6);
  const startMonth = format(startDate, 'MMM');
  const endMonth = format(endDate, 'MMM');
  const startDay = format(startDate, 'd');
  const endDay = format(endDate, 'd');
  const year = format(startDate, 'yyyy');

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`;
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }
};

/**
 * Filter posts for a specific date
 * @param {Array} posts - Array of post objects
 * @param {Date} date - Date to filter by
 * @returns {Array} Filtered posts for the date
 */
export const getPostsForDate = (posts, date) => {
  if (!Array.isArray(posts)) return [];
  return posts?.filter((post) => {
    if (!post.scheduledAt) return false;
    if (post.status === 'draft') return false;
    const postDate = parseISO(post.scheduledAt);
    return isSameDay(postDate, date);
  });
};

/**
 * Check if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  return isTodayDateFns(date);
};

/**
 * Check if a date is in the current month
 * @param {Date} date - Date to check
 * @param {Date} currentDate - Current date to compare against
 * @returns {boolean} True if date is in the same month and year
 */
export const isCurrentMonth = (date, currentDate) => {
  return isSameMonth(date, currentDate) && isSameYear(date, currentDate);
};

export const formatDateHelper = (date, dateFormat) => {
  return format(parseISO(date), dateFormat);
};

/**
 * Convert a local datetime string (yyyy-MM-dd'T'HH:mm) to UTC ISO string
 * @param {string} localDateTimeString
 * @returns {string} UTC ISO string
 */

export const toUtcISOString = (localDateTimeString) => {
  const localDate = new Date(localDateTimeString);
  const utcYear = localDate.getUTCFullYear();
  const utcMonth = String(localDate.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(localDate.getUTCDate()).padStart(2, '0');
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  const utcSeconds = String(localDate.getUTCSeconds()).padStart(2, '0');
  return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds}`;
};

/**
 * Format a Date for native <input type="date|datetime-local"> value (local time)
 * @param {Date|string|null} value - Date or a matching local string
 * @param {('date'|'datetime-local')} type
 * @returns {string}
 */
export const localNativeFormat = (value, type = 'datetime-local') => {
  const fmt = type === 'date' ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm";
  if (!value) return '';
  try {
    if (value instanceof Date) {
      return format(value, fmt);
    }
    if (typeof value === 'string') {
      const parsed = parseLocalNative(value, type);
      return parsed ? format(parsed, fmt) : value;
    }
    return '';
  } catch {
    return '';
  }
};

/**
 * Parse a native local string (yyyy-MM-dd or yyyy-MM-dd'T'HH:mm) to Date
 * @param {string} value
 * @param {('date'|'datetime-local')} type
 * @returns {Date|null}
 */
export const parseLocalNative = (value, type = 'datetime-local') => {
  if (!value) return null;
  try {
    const fmt = type === 'date' ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm";
    return parse(value, fmt, new Date());
  } catch {
    return null;
  }
};

/**
 * Format a Date or ISO string for human display (local time)
 * @param {Date|string} value
 * @param {string} displayFormat
 * @returns {string}
 */
export const formatForDisplay = (value, displayFormat = 'MMM do yyyy HH:mm') => {
  if (!value) return '';
  try {
    if (value instanceof Date) {
      return format(value, displayFormat);
    }
    if (typeof value === 'string') {
      // Try ISO first; fallback to local-native parse
      let d = parseISO(value);
      if (isNaN(d)) {
        d = parseLocalNative(value, 'datetime-local');
      }
      return d ? format(d, displayFormat) : value;
    }
    return '';
  } catch {
    return '';
  }
};

/**
 * Get current timezone offset as "+/-HH:mm"
 * @returns {string}
 */
export const getTimezoneOffsetString = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const abs = Math.abs(offsetMinutes);
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

/**
 * Convert a Date to backend UTC string "YYYY-MM-DD HH:mm:ss"
 * @param {Date} date
 * @returns {string}
 */
export const toUtcISOStringFromDate = (date) => {
  if (!(date instanceof Date)) return '';
  const utcYear = date.getUTCFullYear();
  const utcMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(date.getUTCDate()).padStart(2, '0');
  const utcHours = String(date.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(date.getUTCMinutes()).padStart(2, '0');
  const utcSeconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds}`;
};

/**
 * Check if the current time is within a duration (in minutes) from a start time
 * @param {Date|string} startTime
 * @param {number} durationMinutes
 * @returns {number} Time until expiration in milliseconds, or false if invalid input or expired
 */
export const returnTimeUntilExparation = (startTime, durationMinutes) => {
  if (!startTime || typeof durationMinutes !== 'number') return false;
  if (durationMinutes < 0) return false;
  const start = startTime instanceof Date ? startTime : parseISO(startTime);
  if (isNaN(start)) return false;

  const end = addMinutes(start, durationMinutes);
  const now = new Date();
  const timeUntilExpiration = end - now;

  return timeUntilExpiration;
};

/**
 * Returns a human-readable "time until" string for a future date.
 * e.g. "in 2d 4h 30m", "in 45m", "in 3h 2m", "overdue"
 * @param {string|Date} scheduledAt
 * @returns {string}
 */
export const getTimeUntil = (scheduledAt) => {
  if (!scheduledAt) return '';
  const target = scheduledAt instanceof Date ? scheduledAt : parseISO(scheduledAt);
  if (isNaN(target)) return '';

  const diffMs = target - new Date();
  if (diffMs <= 0) return 'overdue';

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `in ${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
};
